import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { View } from 'react-native'
import Button from '@ca/ui/buttons/Button'
import Instructions from '@ca/ui/features/measurement/Instructions'
import Review from '@ca/ui/features/measurement/Review'
import Typography from '@ca/ui/typography'
import {
  ExpoOpticalMeasureView,
  getOpticalMeasureSettings,
  resolveOpticalMeasureFramingSettings,
} from '../index'
import type {
  FaceDataEvent,
  OpticalMeasureOverlayDataEvent,
  OpticalMeasureOverlayPointKind,
  OpticalMeasureViewHandle,
} from '../index'
import { buildPdDebugScenarios } from './internal/debugScenarios'
import { derivePdViewState } from './internal/deriveViewState'
import { MeasurementFlowDebugControls } from './internal/MeasurementFlowDebugControls'
import { MeasurementPointOverlay } from './internal/MeasurementPointOverlay'
import { useMeasurementFlowSession } from './internal/useMeasurementFlowSession'
import { useMeasurementFramingGuide } from './internal/useMeasurementFramingGuide'
import { usePdMeasurement } from './internal/usePdMeasurement'
import { roundPdMeasurementResult } from './internal/roundMeasurementResults'
import {
  MeasurementCloseButton,
  MeasurementFlowScaffold,
} from './MeasurementFlowScaffold'
import { resolvePdMessages } from './messages'
import type {
  PdFlowViewState,
  PdMeasurementFlowProps,
  PdMeasurementResult,
} from './types'

const PD_OVERLAY_POINT_KINDS = new Set<OpticalMeasureOverlayPointKind>([
  'left-pupil',
  'right-pupil',
])

export function PdMeasurementFlow({
  settings,
  messages: messageOverrides,
  isDev,
  isDebug = false,
  onComplete,
  onCancel,
}: PdMeasurementFlowProps) {
  const messages = resolvePdMessages(messageOverrides)
  const [flowSettings] = useState(() => {
    const defaults = getOpticalMeasureSettings(false)
    const candidate = settings ?? defaults
    const framing = resolveOpticalMeasureFramingSettings(candidate.framing)

    if (framing) return candidate

    return {
      ...candidate,
      framing: defaults.framing,
    }
  })
  const measureViewRef = useRef<OpticalMeasureViewHandle>(null)
  const session = useMeasurementFlowSession<PdFlowViewState>()
  const [result, setResult] = useState<PdMeasurementResult | null>(null)
  // The reviewed result is owned here (mirroring the SH flow) and the frame is
  // frozen at the same completion moment, so no reconciling effect is needed.
  const pd = usePdMeasurement(flowSettings.framing.normal, (completed) => {
    setResult(completed)
    void measureViewRef.current?.freezeCurrentFrame?.()
  })
  const framing = useMeasurementFramingGuide()

  const liveViewState = derivePdViewState({
    phase: pd.phase,
    progress: pd.progress,
    result,
  })
  const viewState = isDev
    ? session.debugScenario?.viewState ?? liveViewState
    : liveViewState

  const isCapturePaused = session.isInstructionsOpen

  // Native publishes overlay data once per camera frame. Storing it re-renders
  // the whole flow — including the open instructions modal, whose markdown is
  // re-parsed on every render — so at frame rate it starves the modal's
  // open/close animation. The overlay only shows on review, where the
  // instructions button is hidden, so dropping these while paused is free.
  const handleOverlayData = (event: OpticalMeasureOverlayDataEvent) => {
    if (isCapturePaused) return
    session.setOverlayData(event.nativeEvent)
  }

  // The flow is remounted for each request, so capture starts once per mount.
  // The instructions can be opened before that happens and `pause` only holds
  // an already-running measurement, so the start waits for the modal to close
  // instead of sampling behind it.
  const hasStartedRef = useRef(false)
  useEffect(() => {
    if (!session.cameraReady || hasStartedRef.current) return
    if (session.isInstructionsOpen) return
    hasStartedRef.current = true
    pd.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.cameraReady, session.isInstructionsOpen])

  const handleRetry = () =>
    session.runRetry(async () => {
      session.setDebugScenario(null)
      await measureViewRef.current?.clearFreeze?.()
      setResult(null)
      pd.clear()
      pd.start()
    })

  const handleCancel = () =>
    session.runCancel(async () => {
      pd.stop()
      try {
        await measureViewRef.current?.clearFreeze?.()
      } finally {
        onCancel()
      }
    })

  // Reading the instructions must not consume the stability window, so the
  // sampling loop is suspended for as long as the modal is up. Nothing is
  // cleared: the accumulated samples and progress survive the pause.
  const handleInstructionsOpen = () => {
    pd.pause()
    session.setIsInstructionsOpen(true)
  }

  const handleInstructionsClose = () => {
    session.setIsInstructionsOpen(false)
    pd.resume()
  }

  const handleConfirm = () => {
    if (!viewState.result) return

    const rounded = roundPdMeasurementResult(viewState.result)
    return session.runConfirm(async () => {
      pd.clear()
      setResult(null)
      await measureViewRef.current?.clearFreeze?.()
      onComplete(rounded)
    })
  }

  const isReviewReady = viewState.status === 'review'
  const isNativeReviewReady = pd.phase === 'complete' && result != null
  const isFailed = viewState.status === 'failed'
  // Available from the first frame — the instructions are what the user needs
  // *before* posing, and opening them pauses the capture anyway. The failed
  // state hands the bottom of the screen over to the retry sheet.
  const isInstructionsVisible = !isReviewReady && !isFailed

  let footer: ReactNode
  if (isFailed) {
    footer = (
      <View className="flex-1">
        <Button
          width="full"
          label={messages.retry}
          variant="outlined"
          disabled={session.isBusy}
          onPress={() => void handleRetry()}
        />
      </View>
    )
  }

  return (
    <>
      <MeasurementFlowScaffold
        message={viewState.message}
        progress={viewState.progress}
        ovalState={viewState.ovalState}
        ovalFrame={framing.ovalFrame}
        isOvalVisible={framing.isReady && pd.phase !== 'complete'}
        isInitializing={!framing.isReady}
        isCloseVisible={!isReviewReady}
        closeLabel={messages.close}
        isCloseDisabled={session.isClosing}
        onClose={() => void handleCancel()}
        isInstructionsVisible={isInstructionsVisible}
        instructionsLabel={messages.instructions}
        onInstructions={handleInstructionsOpen}
        pointOverlay={
          <MeasurementPointOverlay
            data={session.overlayData}
            isVisible={isNativeReviewReady}
            visibleKinds={PD_OVERLAY_POINT_KINDS}
          />
        }
        overlay={
          isDev ? (
            <MeasurementFlowDebugControls
              scenarios={buildPdDebugScenarios()}
              activeKey={session.debugScenario?.key ?? null}
              onSelect={session.setDebugScenario}
            />
          ) : undefined
        }
        isDebug={isDebug}
        camera={
          session.cameraReady ? (
            <ExpoOpticalMeasureView
              ref={measureViewRef}
              style={{ flex: 1 }}
              mode="pd"
              settings={flowSettings}
              nativeOverlayEnabled={false}
              onFramingData={framing.handleFramingData}
              onOverlayData={handleOverlayData}
              onFaceData={(event: FaceDataEvent) =>
                pd.handleFaceData(event.nativeEvent)
              }
            />
          ) : (
            <View className="flex-1 bg-black" />
          )
        }
        footer={footer}
      >
        {isFailed ? (
          <Typography variant="SM" className="text-center text-red-700">
            {messages.failed}
          </Typography>
        ) : null}
      </MeasurementFlowScaffold>

      <Review.Modal
        open={isReviewReady && viewState.result != null}
        measurement="pd"
        results={
          viewState.result
            ? {
                r: { value: viewState.result.rightPd, type: 'automatic' },
                l: { value: viewState.result.leftPd, type: 'automatic' },
              }
            : undefined
        }
        disabled={session.isBusy}
        onSave={() => void handleConfirm()}
        onRepeat={() => void handleRetry()}
        childrenOutside={
          <MeasurementCloseButton
            label={messages.close}
            disabled={session.isClosing}
            onPress={() => void handleCancel()}
          />
        }
      />

      <Instructions.Modal
        open={session.isInstructionsOpen}
        close={handleInstructionsClose}
        measurement="pd"
      />
    </>
  )
}
